import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addWeeks,
  startOfDay,
  startOfHour,
  startOfMinute,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { formatTimelineAnchorTimestamp as formatTimelineAnchorTimestampShared } from "../timestampFormatting.js";

const DEFAULT_SNAP = Object.freeze({
  unit: "minute",
  step: 1,
  mode: "nearest",
});

function normalizeSnap(snap) {
  if (typeof snap === "number") {
    return {
      unit: "minute",
      step: Math.max(1, Math.round(snap)),
      mode: "nearest",
    };
  }

  if (!snap || typeof snap !== "object") {
    return DEFAULT_SNAP;
  }

  const unit =
    snap.unit === "hour" ||
    snap.unit === "day" ||
    snap.unit === "week" ||
    snap.unit === "month"
      ? snap.unit
      : "minute";
  const step = Math.max(1, Math.round(Number(snap.step) || 1));
  const mode = snap.mode === "floor" || snap.mode === "ceil" ? snap.mode : "nearest";

  return { unit, step, mode };
}

function addStep(date, unit, step) {
  if (unit === "minute") return addMinutes(date, step);
  if (unit === "hour") return addHours(date, step);
  if (unit === "day") return addDays(date, step);
  if (unit === "week") return addWeeks(date, step);
  return addMonths(date, step);
}

function alignToStepStartLocal(date, unit, step) {
  const safeStep = Math.max(1, step);

  if (unit === "minute") {
    const aligned = startOfMinute(date);
    const minute = aligned.getMinutes();
    aligned.setMinutes(minute - (minute % safeStep), 0, 0);
    return aligned;
  }

  if (unit === "hour") {
    const aligned = startOfHour(date);
    const hour = aligned.getHours();
    aligned.setHours(hour - (hour % safeStep), 0, 0, 0);
    return aligned;
  }

  if (unit === "day") {
    const aligned = startOfDay(date);
    const dayOfMonth = aligned.getDate();
    aligned.setDate(dayOfMonth - ((dayOfMonth - 1) % safeStep));
    return aligned;
  }

  if (unit === "week") {
    const aligned = startOfWeek(date);
    if (safeStep === 1) return aligned;

    const anchor = startOfWeek(new Date(0));
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const diffWeeks = Math.floor((aligned.getTime() - anchor.getTime()) / weekMs);
    const remainder = ((diffWeeks % safeStep) + safeStep) % safeStep;
    return addWeeks(aligned, -remainder);
  }

  const aligned = startOfMonth(date);
  if (safeStep === 1) return aligned;

  const absoluteMonth = aligned.getFullYear() * 12 + aligned.getMonth();
  const remainder = ((absoluteMonth % safeStep) + safeStep) % safeStep;
  return addMonths(aligned, -remainder);
}

export function snapDateToResolutionLocal(date, snap) {
  const normalized = normalizeSnap(snap);
  const floorDate = alignToStepStartLocal(date, normalized.unit, normalized.step);

  if (normalized.mode === "floor") return floorDate;
  if (date.getTime() <= floorDate.getTime()) return floorDate;

  const ceilDate = addStep(floorDate, normalized.unit, normalized.step);
  if (normalized.mode === "ceil") return ceilDate;

  const floorDistance = date.getTime() - floorDate.getTime();
  const ceilDistance = ceilDate.getTime() - date.getTime();
  return floorDistance <= ceilDistance ? floorDate : ceilDate;
}

export function formatTimelineAnchorTimestamp(dateValue) {
  return formatTimelineAnchorTimestampShared(dateValue);
}

export function formatDropTimestamp(date, snap) {
  const normalized = normalizeSnap(snap);
  if (normalized.unit === "minute" || normalized.unit === "hour") {
    return date.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatSnapResolution(snap) {
  const normalized = normalizeSnap(snap);

  if (normalized.unit === "minute") return `${normalized.step}m`;
  if (normalized.unit === "hour") return `${normalized.step}h`;
  if (normalized.unit === "day") return `${normalized.step}d`;
  if (normalized.unit === "week") return `${normalized.step}w`;
  return `${normalized.step}mo`;
}
